/** Student welcome-meeting form section. */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { Checkbox } from '@/components/forms/Checkbox';
import { DateTimePicker } from '@/components/forms/DateTimePicker';
import { MeetingSelector } from '@/components/common/MeetingSelector';
import {
  buildWelcomeMeetingParticipationPatch,
  shouldShowWelcomeParticipationFields,
} from '@/components/students/remote/student-form-utils';

/** Props for {@link StudentWelcomeMeetingSection}. */
type StudentWelcomeMeetingSectionProps = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  userId: string;
  companyId: string;
  mode: 'create' | 'edit';
  user: any;
  participationLocked?: boolean;
}>;

/** Student welcome-meeting form section. */
export function StudentWelcomeMeetingSection({
  formData,
  onChange,
  userId,
  companyId,
  mode,
  user,
  participationLocked = false,
}: StudentWelcomeMeetingSectionProps) {
  const showParticipationFields = shouldShowWelcomeParticipationFields(formData);
  const participationHint = participationLocked
    ? 'Participation enregistrée — modification non autorisée.'
    : 'Participation déjà effectuée — inscription à une séance non requise.';

  const handleParticipationChange = (checked: boolean) => {
    // ZDCD-546: once participation is saved, it cannot be cancelled from the edit form
    const patch = buildWelcomeMeetingParticipationPatch(
      checked,
      formData,
      user,
      participationLocked,
    );
    if (!patch) return;
    for (const [key, value] of Object.entries(patch)) {
      onChange(key, value);
    }
  };

  function handleSessionIdChange(id: string | null): void {
    onChange('sessionId', id);
  }

  function handleParticipatedAtChange(value: string): void {
    onChange('participatedAt', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Calendar className="w-4 h-4" />
        Réunion d'accueil
      </h4>
      <div className="space-y-4">
        {!formData.hasParticipated && (
          <MeetingSelector
            type="welcome_meeting"
            userId={userId ?? ''}
            companyId={companyId ?? ''}
            value={formData.sessionId ?? null}
            onChange={handleSessionIdChange}
            mode={mode}
          />
        )}
        {formData.hasParticipated && (
          <p className="text-sm text-muted-foreground">{participationHint}</p>
        )}
        {showParticipationFields && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
            <Checkbox
              label="A participé"
              checked={formData.hasParticipated === true}
              onCheckedChange={handleParticipationChange}
              disabled={participationLocked}
              size="sm"
            />
            <DateTimePicker
              label="Date participation"
              value={formData.participatedAt ?? ''}
              onChange={handleParticipatedAtChange}
              disabled={participationLocked}
            />
          </div>
        )}
      </div>
    </div>
  );
}
